/** Shared class recipes — copied verbatim from GridPreview / projections-grid.md */

import { loadSchema } from './engine/schema'

export const ROW_HEIGHT = 54
export const INPUT_ROW_HEIGHT = 54

const ROW_BASE =
  '[&>td]:!px-2 [&>td]:!py-0 [&>td]:!align-middle [&>td]:overflow-hidden [&>td]:whitespace-nowrap [&>th]:overflow-hidden [&>th]:whitespace-nowrap [&>th]:border-0 [&>td:nth-child(2)]:border-0 [&>td:nth-child(n+3)]:border-b [&>td:nth-child(n+3)]:border-r [&>td:nth-child(n+3)]:border-gray-200'

export const ROW = `group h-[54px] max-h-[54px] ${ROW_BASE}`
/** Group headers — value columns get a bottom rule only (no vertical cell borders). */
export const GROUP_ROW =
  'group h-[54px] max-h-[54px] [&>td]:!px-2 [&>td]:!py-0 [&>td]:!align-middle [&>td]:overflow-hidden [&>td]:whitespace-nowrap [&>th]:overflow-hidden [&>th]:whitespace-nowrap [&>th]:border-0 [&>td:nth-child(2)]:border-x-0 [&>td:nth-child(2)]:border-t-0 [&>td:nth-child(n+3)]:border-b [&>td:nth-child(n+3)]:border-gray-200'
export const INPUT_ROW = `group h-[54px] max-h-[54px] [&>td]:!px-2 [&>td]:!py-1 [&>td]:!align-middle [&>td]:overflow-hidden [&>td]:whitespace-nowrap [&>th]:overflow-hidden [&>th]:whitespace-nowrap [&>th]:border-0 [&>td:nth-child(2)]:border-0 [&>td:nth-child(n+3)]:border-b [&>td:nth-child(n+3)]:border-r [&>td:nth-child(n+3)]:border-gray-200`
export const STICKY_LABEL = 'sticky left-0 z-20 bg-white group-hover:bg-gray-50'
export const STICKY_DRIVER = 'sticky z-20 bg-white group-hover:bg-gray-50 table-locked-edge'
export const PROJ_EDGE = 'border-l border-l-gray-200'
/** Gap / section-pad rows — no cell or row borders (see index.css `.proj-grid-spacer`). */
export const SPACER_ROW = 'proj-grid-spacer'
/** Row class — 1px top rule on every cell (label, driver, months). See index.css `.proj-row-top-rule`. */
export const ROW_RULE_TOP = 'proj-row-top-rule'
/** Value-cell top rule when a sheet section resumes mid-block (e.g. Payroll - Wages). */
export const RULE_SUBTOTAL = '!border-t !border-t-gray-900'
export const RULE_TOTAL_BOTTOM = 'border-b border-b-gray-300'
/** First line after a blank spacer row in the sheet (e.g. Payroll - Wages). */
export const SECTION_START_KEYS = new Set(['is.payroll_wages'])
export const LOCKED_DRIVER_SELECT =
  'w-full min-w-0 [&>button]:!bg-gray-100 [&>button]:!text-gray-600 [&>button]:hover:!border-gray-200'
/** Main section bands (Operational Data, Income Statement, Balance Sheet). */
export const SECTION_BAND =
  'proj-section-band text-white border-b border-r border-gray-200 cursor-pointer select-none'
/** Sub-section group headers (Location Data, Core Metrics, Assets, …). */
export const GROUP_HEADER = 'bg-green-50 text-green-800 border-b border-gray-200'
export const GROUP_STICKY_LABEL = 'sticky left-0 z-20 bg-green-50 group-hover:bg-green-50'
export const GROUP_STICKY_DRIVER = 'sticky z-20 bg-green-50 group-hover:bg-green-50'
export const GROUP_HEADER_GREY = 'bg-gray-100 text-gray-800 border-b border-gray-200'
export const GROUP_STICKY_LABEL_GREY = 'sticky left-0 z-20 bg-gray-100 group-hover:bg-gray-100'
export const GROUP_STICKY_DRIVER_GREY = 'sticky z-20 bg-gray-100 group-hover:bg-gray-100'
export const GREYED = '!bg-gray-100 !text-gray-600'
/** Historical month columns — matches thead `bg-gray-50` / `text-gray-600`. */
export const HIST_COL = '!bg-gray-50 !text-gray-600'
export const VALUE = 'text-right text-sm tabular-nums text-gray-900'
export const SUB_VALUE = 'text-xs leading-4 tabular-nums text-gray-500'
export const COMPARE_CHIP =
  'inline-flex items-center rounded-sm border border-gray-300 bg-white px-1 py-0 text-[10px] font-medium leading-none text-gray-600 hover:border-gray-400'
export const COMPARE_CHIP_ON =
  'inline-flex items-center rounded-sm border border-gray-900 bg-gray-900 px-1 py-0 text-[10px] font-medium leading-none text-white'
export const CELL_INPUT =
  'input box-border h-7 w-full min-w-0 px-1.5 text-right text-sm tabular-nums ' +
  'focus:outline-none focus:ring-2 focus:ring-brand-600'
/** HoverHelp draws a dashed underline on its trigger; markers are their own shape, so suppress it. */
export const BARE_TRIGGER = '[&_.tooltip-trigger]:!border-b-0 [&_.tooltip-trigger]:!no-underline'
export const GREEN_STATUS_TAG =
  'inline-flex items-center rounded-sm border border-green-200 bg-green-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-green-800'
export const RED_STATUS_TAG =
  'inline-flex items-center rounded-sm border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-red-800'

export const W_MONTH = 112
export const NOTE_CELL_BG = 'proj-note-cell-bg'
export const OVERRIDE_CELL_BG = 'proj-override-cell'

export const BAND_KEYS = new Set([
  'is.operational_data',
  'is.income_statement',
  'bs.balance_sheet',
  'cf.cash_flow_statement',
])

export const METRIC_KEYS = new Set([
  'is.gross_margin_pct',
  'is.operating_margin_pct',
  'is.net_margin_pct',
  'is.ebitdar_margin_pct',
  'is.ebitda_margin_pct',
])

/** Per-dollar rates and tickets — full currency in the grid (not $000s). */
export const USD_FULL_FORMAT_KEYS = new Set([
  'is.avg_ticket',
  'is.avg_hourly_rate_boh',
  'is.avg_hourly_rate_foh',
])

export const USD_SALARY_K_FORMAT_KEYS = new Set(['is.avg_salary_annual'])

export type GridDisplayUnit = 'usd' | 'usd_full' | 'usd_salary_k' | 'count' | 'pct'

export function gridDisplayUnit(
  lineKey: string,
  unit: 'usd' | 'count' | 'pct' | 'text',
): GridDisplayUnit {
  const base = unit === 'text' ? 'usd' : unit
  if (base === 'usd' && USD_SALARY_K_FORMAT_KEYS.has(lineKey)) {
    return 'usd_salary_k'
  }
  if (base === 'usd' && USD_FULL_FORMAT_KEYS.has(lineKey)) {
    return 'usd_full'
  }
  return base
}

export const GRAND_TOTAL_KEYS = new Set([
  'is.net_income_loss',
  'bs.total_assets',
  'bs.total_liabilities_and_equity',
  'bs.plus_net_income',
])

/** Fixed rows that get a ledger top rule and semibold weight on month columns. */
export const LEDGER_RULE_KEYS = new Set(['is.gross_product_sales'])

const STATIC_FIXED_DRIVER_LABELS: Record<string, string> = {
  'is.gross_product_sales': 'Orders × Avg Ticket',
  'is.total_orders': 'Avg Orders × Days Open',
  'bs.cash_bank_accounts_and_petty_cash': 'Cash plug',
  'bs.less_accumulated_depreciation': 'Linked: Depreciation Expense',
  'bs.less_accumulated_amortization': 'Linked: Amortization Expense',
  'bs.plus_net_income': 'Linked: Net Income',
}

function computedFixedLabels(): Record<string, string> {
  const labels: Record<string, string> = {}
  for (const entry of loadSchema()) {
    if (entry.computed && entry.fixed_label) {
      labels[entry.key] = entry.fixed_label
    }
  }
  return labels
}

export const FIXED_DRIVER_LABELS: Record<string, string> = {
  ...STATIC_FIXED_DRIVER_LABELS,
  ...computedFixedLabels(),
}

export const LINKED_TOTAL_KEYS = new Set([
  'bs.less_accumulated_depreciation',
  'bs.less_accumulated_amortization',
  'bs.plus_net_income',
])

/** Plan §3.3 driver table order — filter to allowed drivers per line. */
export const PICKER_DRIVER_ORDER = [
  'carry_forward',
  'ignore',
  'count_value',
  'dollar_value',
  'count_per_calendar_day',
  'dollar_per_calendar_day',
  'dollar_per_pay_period',
  'labor_buildup',
  'count_per_open_day',
  'dollar_per_open_day',
  'dollar_per_sq_ft',
  'dollar_per_1000_sqft_calendar_day',
  'dollar_per_fte',
  'mom_pct',
  'mom_dollar',
  'yoy_pct',
  'yoy_dollar',
  'pct_gross_sales',
  'pct_gross_sales_growth',
  'pct_of_line',
  'dollar_per_line_unit',
  's_curve',
  'dio',
  'dso',
  'dpo',
] as const

export type PickerDriver = (typeof PICKER_DRIVER_ORDER)[number]

export const DRIVER_LABELS: Record<PickerDriver, string> = {
  carry_forward: 'Carry forward',
  ignore: 'Ignore',
  count_value: 'Count',
  dollar_value: '$ value',
  count_per_calendar_day: 'Count per calendar day',
  dollar_per_calendar_day: '$ per calendar day',
  dollar_per_pay_period: '$ per pay period',
  labor_buildup: 'Labor build-up',
  count_per_open_day: 'Count per open day',
  dollar_per_open_day: '$ per open day',
  dollar_per_sq_ft: '$ per sq ft',
  dollar_per_1000_sqft_calendar_day: '$ per 1,000 sq ft-day',
  dollar_per_fte: '$ per FTE',
  mom_pct: '% MoM',
  mom_dollar: '$ MoM',
  yoy_pct: '% YoY',
  yoy_dollar: '$ YoY',
  pct_gross_sales: '% of gross sales',
  pct_gross_sales_growth: '% of gross sales growth',
  pct_of_line: '% of line',
  dollar_per_line_unit: '$ per ref unit',
  s_curve: 'S-curve',
  dio: 'DIO (days)',
  dso: 'DSO (days)',
  dpo: 'DPO (days)',
}

export function indentStyle(indent: number): { paddingLeft: string } {
  return { paddingLeft: `${indent}rem` }
}

export function rowHeight(kind: GridRowKind): number {
  if (kind === 'sectionPad') return 20
  if (kind === 'gap') return 16
  if (kind === 'input') return INPUT_ROW_HEIGHT
  return ROW_HEIGHT
}

export type GridRowKind =
  | 'band'
  | 'gap'
  | 'sectionPad'
  | 'group'
  | 'text'
  | 'computed'
  | 'cf_tie_check'
  | 'greyed'
  | 'input'

export type ComputedWeight = 'fixed' | 'subtotal' | 'total' | 'metric' | 'derived'

export type GridRowModel =
  | { kind: 'band'; label: string; sectionKey: string }
  | { kind: 'gap' }
  | { kind: 'sectionPad'; sectionKey: string }
  | { kind: 'group'; key: string; label: string; indent: number; tone?: 'green' | 'grey' }
  | {
      kind: 'text'
      key: string
      label: string
      indent: number
      unit: 'text'
    }
  | {
      kind: 'computed'
      key: string
      label: string
      indent: number
      unit: GridDisplayUnit
      weight: ComputedWeight
      driverLabel: string
      driverHelp?: string
      /** Skip the ledger top rule when this subtotal is the first row after a band or group header. */
      suppressTopRule?: boolean
    }
  | {
      kind: 'cf_tie_check'
      key: string
      label: string
      indent: number
    }
  | {
      kind: 'greyed'
      key: string
      label: string
      indent: number
      unit: GridDisplayUnit
      scope: 'row' | 'projected'
      driverLabel: string
    }
  | {
      kind: 'input'
      key: string
      label: string
      indent: number
      unit: GridDisplayUnit
      valueUnit: GridDisplayUnit
      driverLocked?: boolean
    }
